// src/pages/LoginPage.tsx

/**
 * Login page for the Quantik Mind enterprise frontend.
 *
 * This page is responsible for:
 * - collecting user credentials
 * - triggering the multi-tenant authentication flow
 * - updating the shared authentication context
 * - clearing any previously selected project after login
 * - redirecting the user to the main application shell
 *
 * The visual design is intentionally preserved while improving code clarity,
 * documentation, and consistency.
 */

import React, { useContext, useState } from 'react';
import {
  Alert,
  Box,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';

import logo from '../assets/logo_no_bg.png';
import {
  getMe,
  login,
  LoginCredentials,
  LoginResponse,
} from '../api/auth';
import { AuthContext } from '../context/authContext';
import { useProject } from '../context/ProjectContext';
import { normalizeUserFacingError } from '../utils/normalizeUserFacingError';
import ActionButton from '../components/common/ActionButton';

/**
 * Render the enterprise login page.
 *
 * @returns Login page component.
 */
export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const { setCurrentProject } = useProject();

  /**
   * Authenticate the user and initialize the frontend session state.
   *
   * On success, the method:
   * - stores normalized auth data in the shared auth context
   * - clears any previously selected project
   * - redirects the user to the dashboard
   */
  const handleLogin = async () => {
    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const credentials: LoginCredentials = { email, password };
      const response: LoginResponse = await login(credentials);
      let profile = null;

      try {
        profile = await getMe();
      } catch (profileError) {
        console.warn('[LOGIN] Failed to load /auth/me after login', profileError);
      }

      /**
       * Persist the normalized authentication payload required by the frontend
       * session model.
       */
      const nextAuthData = {
        userId: response.user.id,
        username: response.user.username,
        email: response.user.email,
        fullName: profile?.full_name || response.user.full_name,
        organizationId: response.organization.id,
        organizationName: profile?.organization_name || response.organization.name,
        organizationSlug: profile?.organization_slug || response.organization.slug,
        isAdmin: Boolean(profile?.is_admin ?? profile?.admin ?? response.admin),
        isInternalAdmin: profile?.is_internal_admin || false,
        emailVerified: Boolean(profile?.email_verified ?? response.user.email_verified),
      };
      auth?.setAuthData(nextAuthData);

      /**
       * Clear the previously selected project to avoid carrying over tenant or
       * session-specific project state across logins.
       */
      setCurrentProject(null);

      /**
       * Redirect the user to the main dashboard after successful login.
       */
      navigate(nextAuthData.emailVerified ? '/' : '/verify-email/check');
    } catch (loginError: any) {
      console.error('[LOGIN] Error:', loginError);
      setError(normalizeUserFacingError(loginError));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Trigger login when the user presses Enter in one of the form fields.
   *
   * @param event - Keyboard event raised by the input field.
   */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      void handleLogin();
    }
  };

  /**
   * Toggle password visibility in the password field.
   */
  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0A0E14 0%, #13171F 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          width: '150%',
          height: '150%',
          background:
            'radial-gradient(circle at 30% 50%, rgba(57, 255, 20, 0.1) 0%, transparent 50%)',
          animation: 'pulse 8s ease-in-out infinite',
        },
        '@keyframes pulse': {
          '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
          '50%': { transform: 'translate(-5%, -5%) scale(1.1)' },
        },
      }}
    >
      <Paper
        elevation={24}
        className="page-transition"
        sx={{
          maxWidth: 400,
          width: '90%',
          p: 4,
          borderRadius: 4,
          background: 'rgba(19, 23, 31, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid #39ff14',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Brand identity block displayed above the login form. */}
        <Stack alignItems="center" spacing={2} mb={4}>
          <Box
            sx={{
              width: 64,
              height: 64,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={logo}
              alt="Quantik Mind logo"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          </Box>

          <Box textAlign="center">
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{
                color: '#E6EDF3',
                letterSpacing: '-0.02em',
                mb: 0.5,
                fontSize: '1.5rem',
              }}
            >
              Quantik Mind
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Quantum-inspired Test Intelligence
            </Typography>
          </Box>
        </Stack>

        {/* Authentication error feedback. */}
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              bgcolor: 'rgba(255, 61, 61, 0.1)',
              border: '1px solid rgba(255, 61, 61, 0.3)',
              '& .MuiAlert-icon': {
                color: '#FF3D3D',
              },
            }}
          >
            {error}
          </Alert>
        )}

        {/* Login form fields and submit action. */}
        <Stack spacing={3}>
          <TextField
            label="Email Address"
            type="email"
            fullWidth
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            autoComplete="email"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255, 255, 255, 0.02)',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(57, 255, 20, 0.4)',
                  borderWidth: 1,
                },
                '& input': {
                  color: '#E6EDF3',
                },
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 100px #161B22 inset',
                  WebkitTextFillColor: '#E6EDF3',
                },
              },
            }}
          />

          <TextField
            label="Password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            autoComplete="current-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                    size="small"
                    sx={{ color: 'text.secondary' }}
                  >
                    {showPassword ? (
                      <VisibilityOffIcon />
                    ) : (
                      <VisibilityIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255, 255, 255, 0.02)',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(57, 255, 20, 0.4)',
                  borderWidth: 1,
                },
                '& input': {
                  color: '#E6EDF3',
                },
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 100px #161B22 inset',
                  WebkitTextFillColor: '#E6EDF3',
                },
              },
            }}
          />

          <ActionButton
            onClick={() => void handleLogin()}
            fullWidth
            size="large"
            disabled={loading}
            sx={{
              py: 1.25,
              fontSize: '0.9375rem',
            }}
          >
            {loading ? 'Authenticating...' : 'Sign In'}
          </ActionButton>
        </Stack>

        {/* Progress indicator shown while the authentication request is running. */}
        {loading && (
          <LinearProgress
            sx={{
              mt: 3,
              borderRadius: 2,
              height: 4,
              bgcolor: 'rgba(57, 255, 20, 0.1)',
              '& .MuiLinearProgress-bar': {
                bgcolor: '#39ff14',
                borderRadius: 2,
              },
            }}
          />
        )}

        {/* Page footer. */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Test smarter, not harder
          </Typography>
          <Box sx={{ mt: 1.5 }}>
            <ActionButton
              onClick={() => navigate('/signup')}
            >
              Create account
            </ActionButton>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
